import { useAppSelector } from "@hooks/useAppSelector";
import useDisclosure from "@hooks/useDisclosure";
import usePostcodeValidator from "@hooks/usePostalcodeValidator";
import { Input } from "@lib/Input";
import { Spinner } from "@lib/Loader";
import { Select } from "@lib/Select";
import { useAddressBook } from "@network/hooks/shipment";
import { ICreateShipment } from "@network/interface";
import { FormikErrors } from "formik";
import { CountryCode } from "postcode-validator";
import React from "react";
import { twMerge } from "tailwind-merge";

interface IProps {
  handleChange: any;
  values: ICreateShipment;
  errors: FormikErrors<ICreateShipment>;
  streets: { key: string; value: string }[];
  isLoadingReceiverStreets?: boolean;
  handleInputChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: any;
}

export const ReceiverDetailsForm = ({
  errors,
  handleChange,
  values,
  streets,
  isLoadingReceiverStreets,
  handleInputChange,
  setFieldValue,
}: IProps) => {
  const { addresses } = useAddressBook();
  const { isValid: to_valid } = usePostcodeValidator(
    values.receiver_postal_code,
    CountryCode.GB
  );
  const handleUseLocalAddress = useDisclosure();

  const handleNumberChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (isNaN(value)) {
      return;
    } else {
      handleChange({
        target: { name, value },
      });
    }
  };

  const isLoggedIn = useAppSelector((state) => state.user.access_token);
  return (
    <div>
      <p className="text-lg">Please fill in the consignee details (receiver)</p>
      <div className="mt-5">
        <div className="w-full flex flex-col md:flex-row justify-between">
          <div className="w-full md:w-[48%]">
            <Input
              label="Receiver's Fullname"
              placeholder="Receiver's Fullname"
              required
              name="receiver_fullname"
              onChange={handleChange}
              value={values.receiver_fullname}
              error={errors?.receiver_fullname}
            />
          </div>
          <div className="w-full md:w-[48%]">
            <Input
              label="Receiver Email Address"
              placeholder="receiver@gmail.com"
              required
              type="email"
              name="receiver_email_address"
              onChange={handleChange}
              value={values.receiver_email_address}
              error={errors?.receiver_email_address}
            />
          </div>
        </div>
        <div className="w-full flex flex-col md:flex-row justify-between">
          <div className="w-full md:w-[48%]">
            <Select
              options={[{ key: "United Kingdom", value: "UK" }]}
              value={values.receiver_country}
              error={errors.receiver_country}
              label="Receiver Country"
              name="receiver_country"
              placeholder="Select receiver Country"
              onChange={handleChange}
            />
          </div>
          <div className="w-full md:w-[48%]">
            <Input
              label="Receiver Mobile Number"
              placeholder="+44 567 890 1112"
              required
              max={12}
              name="receiver_phone_number"
              type="number"
              pattern="[0-9]*"
              inputMode="numeric"
              onChange={handleNumberChange}
              value={values.receiver_phone_number}
              error={errors?.receiver_phone_number}
            />
          </div>
        </div>
        <div
          className={twMerge(
            "w-full flex flex-col md:flex-row justify-between",
            isLoggedIn ? "mb-4" : ""
          )}
        >
          <div className="w-full">
            {handleUseLocalAddress.isOpen ? (
              <Select
                label="Receiver Address"
                options={addresses?.receiverAddressBook?.map((add: any) => ({
                  key: add.postal_code,
                }))}
                onChange={(e) => {
                  setFieldValue("receiver_postal_code", e.target.value);
                  handleUseLocalAddress.onClose();
                }}
                placeholder="Select Receiver Address"
              />
            ) : (
              <Input
                label="Receiver Postal Code"
                placeholder="Postal code"
                required
                name="receiver_postal_code"
                onChange={handleInputChange}
                value={values.receiver_postal_code}
                error={
                  errors?.receiver_postal_code || to_valid
                    ? ""
                    : "Invalid postal code"
                }
              />
            )}
            {isLoggedIn ? (
              <p className="text-xs">
                Use address from{" "}
                <span
                  className={twMerge(
                    "text-primary-500 font-bold cursor-pointer capitalize",
                    handleUseLocalAddress.isOpen ? "text-red-500" : ""
                  )}
                  onClick={() => {
                    if (handleUseLocalAddress.isOpen) {
                      handleUseLocalAddress.onClose();
                    } else {
                      handleUseLocalAddress.onOpen();
                    }
                  }}
                >
                  {handleUseLocalAddress.isOpen ? "Cancel" : "Address Book"}
                </span>
              </p>
            ) : null}
          </div>
        </div>
        {!handleUseLocalAddress.isOpen ? (
          <div className="w-full flex flex-col md:flex-row justify-between">
            <div className="w-full md:w-[48%]">
              <Select
                options={streets}
                label="Street Name"
                placeholder="Select Street"
                required
                disabled={isLoadingReceiverStreets || streets.length <= 0}
                name="receiver_street_name"
                onChange={handleChange}
                value={values.receiver_street_name}
                error={errors?.receiver_street_name}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
